import React, { useState } from 'react';
import { useForm } from '../context/FormContext';
import { useAppConfig } from '../context/AppConfigContext';
import { joinWaitlist } from '../services/geo';

interface StepGatoComingSoonProps {
  /** Optional override for the back action. Falls back to setCurrentStep(1) for backwards compatibility. */
  onBack?: () => void;
}

/**
 * Pantalla simple para usuarios que eligen Gato. La línea de comida natural
 * para gatos aún no está lista. Capturamos su email y los avisamos cuando
 * salga. Mantenemos el chrome al mínimo (sin "Paso 2 de 2", sin marco
 * anidado, sin link a tienda) por petición de Santiago: la pantalla debe
 * ser una sola unidad clara, no un funnel dentro del funnel.
 */
export const StepGatoComingSoon: React.FC<StepGatoComingSoonProps> = ({ onBack }) => {
  const { formData, setCurrentStep } = useForm();
  const { t, config, logoImage } = useAppConfig();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  const catImage = config.funnel_config.translations?.['step1.image.cat'] || '/images/funnel/cat-illustration.png';
  const nombre = formData.nombre || 'tu gato';

  const handleSubscribe = async () => {
    if (submitting || !email.trim() || !email.includes('@')) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await joinWaitlist({
        email: email.trim(),
        petName: nombre,
        animalType: 'gato',
        country: 'ES',
        context: 'gato-sin-receta',
      });
      setAlreadyRegistered(!!res?.already_registered);
      setSubscribed(true);
    } catch {
      setSubmitError(t('gato.submit_error', 'No pudimos guardar tu email. Inténtalo de nuevo.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      setCurrentStep(1);
    }
  };

  const emailValido = email.trim().length > 0 && email.includes('@');

  return (
    <div className="h-screen bg-[#F7F4EE] flex flex-col overflow-hidden" style={{ height: '100dvh' }}>
      {/* Header — solo logo, sin contador de pasos. La pantalla no es parte
          del funnel propiamente dicho, así que mostrar "Paso 2 de 2" daba
          falsa sensación de progreso. */}
      <header className="bg-white px-4 sm:px-6 py-2 flex-shrink-0">
        <button
          type="button"
          onClick={handleBack}
          aria-label={t('gato.logo_home_aria', 'Volver al inicio de la calculadora')}
          className="rounded-lg focus:outline-none focus:ring-2 focus:ring-[#008BBC]/40"
        >
          <img src={logoImage} alt="Milo y Lola" className="h-8 sm:h-10 object-contain" />
        </button>
      </header>

      {/* Content — alineado arriba (no vertical-center) para que en PC no
          parezca una tarjetita en medio de la pantalla. Santiago: "que
          salga arriba, sin frame de móvil". Sin marco interno: el fondo
          crema ya delimita la pantalla. */}
      <div className="flex-1 flex items-start justify-center px-4 pt-10 sm:pt-16 min-h-0 overflow-y-auto">
        <div className="w-full max-w-md text-center space-y-5">
          <img src={catImage} alt="Gato" className="w-24 h-24 sm:w-28 sm:h-28 mx-auto object-contain" />

          <div className="space-y-2">
            <h1 className="font-fredoka text-2xl sm:text-3xl font-semibold text-[#1a1a1a]">
              {t('gato.title_v2', 'Próximamente para {nombre}', { nombre })}
            </h1>
            <p className="font-poppins text-sm text-black/60 leading-snug">
              {t('gato.subtitle_v2', 'Estamos preparando recetas naturales pensadas también para gatos.')}
            </p>
          </div>

          {!subscribed ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubscribe();
              }}
              className="space-y-3"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('gato.email_placeholder', 'Tu email')}
                className="w-full px-4 py-3 bg-white rounded-2xl font-poppins text-base text-center placeholder:text-black/30 border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#008BBC]/30 focus:border-[#008BBC]"
                autoComplete="email"
                inputMode="email"
              />
              {submitError && (
                <p role="alert" className="font-poppins text-sm text-red-600">
                  {submitError}
                </p>
              )}
              <button
                type="submit"
                disabled={!emailValido || submitting}
                className="w-full px-6 py-3 rounded-xl font-fredoka text-base font-semibold bg-[#008BBC] text-white hover:bg-[#007aa8] active:scale-[0.98] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-all shadow-md"
              >
                {submitting
                  ? t('gato.notify_submitting', 'Enviando…')
                  : t('gato.notify_button_v2', '¡Quiero que me aviséis cuando salga!')}
              </button>
            </form>
          ) : (
            <div className="bg-[#008BBC]/10 border border-[#008BBC]/30 rounded-2xl p-4">
              <p className="font-fredoka text-base text-[#008BBC] font-medium">
                {alreadyRegistered
                  ? t('gato.subscribed_already', 'Ya estabas en nuestra lista; hemos actualizado tus datos.')
                  : t('gato.subscribed_v2', '¡Perfecto! Te avisaremos en cuanto esté listo.')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom nav — botón Volver prominente y siempre visible. */}
      <div
        className="bg-white border-t border-gray-200 px-4 py-3 flex-shrink-0"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
      >
        <div className="max-w-md mx-auto">
          <button
            type="button"
            onClick={handleBack}
            className="w-full px-6 py-3 rounded-xl font-fredoka text-base font-semibold text-[#008BBC] border-2 border-[#008BBC]/30 hover:bg-[#008BBC]/5 active:scale-[0.98] transition-all"
          >
            &larr; {t('gato.back_to_pet', 'Volver a elegir mascota')}
          </button>
        </div>
      </div>
    </div>
  );
};
